import { AttachmentDetail_ContentLocation_Field, AttachmentDetail_Status } from '@policyfly/protobuf/patrick'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useLatestRequest } from '@/composables/latestRequest'
import { useApiStore } from '@/stores/api'

import { contentLocationField } from '@/utils/attachments'
import { hotReloadStore } from '@/utils/build'

import type { DocumentHistory, OverrideRun, Preview, PreviewPiece } from '@policyfly/protobuf'
import type { SubmitDocumentOverrideRequest } from '@policyfly/protobuf/patrick'
import type { ComputedRef, Ref } from 'vue'

interface CachedFiles {
  pdf?: Promise<File>
  original?: Promise<File>
}

export interface DocReviewStore {
  /**
   * All document histories.
   */
  histories: Ref<DocumentHistory[]>
  /**
   * Get a specific document history by ID.
   */
  historyById: ComputedRef<(id: string) => DocumentHistory | undefined>
  /**
   * Whether document history list request is in progress.
   */
  isListing: Ref<boolean>
  /**
   * Get all document histories.
   */
  list: (attachmentIds: string[]) => Promise<void>
  /**
   * Get a piece from the current preview by its ID.
   */
  pieceById: ComputedRef<(id: string) => PreviewPiece | undefined>
  /**
   * Returns the cached PDF for a piece ID or an upload/revision attachment ID.
   */
  getFile: (id: string) => Promise<File>
  /**
   * Returns a piece or revision in its original format, independently of the cached PDF preview.
   */
  getOriginalFile: (id: string) => Promise<File>
  /**
   * The current previewed version.
   */
  version: Ref<Preview | null>
  /**
   * Whether document preview request is in progress.
   */
  isPreviewing: ComputedRef<boolean>
  /**
   * Whether document override is in progress.
   */
  isSaving: Ref<boolean>
  /**
   * Get document preview.
   */
  preview: (attachmentId: string, refresh?: boolean) => Promise<Preview | null>
  /**
   * Save document override.
   */
  save: (request: SubmitDocumentOverrideRequest) => Promise<OverrideRun>
  /**
   * Aborts active preview request.
   */
  cancel: () => void
  /**
   * Whether override runs are being refreshed.
   */
  isRefreshing: Ref<boolean>
  /**
   * Get a specific override run by ID.
   */
  runsById: ComputedRef<(id: string) => OverrideRun | undefined>
  /**
   * Get all override runs.
   */
  refresh: (runId: string) => Promise<OverrideRun>
}

export const useDocReviewStore = defineStore('docReview', (): DocReviewStore => {
  const apiStore = useApiStore()

  const histories: DocReviewStore['histories'] = ref([])
  const isListing = ref(false)

  const historyById: DocReviewStore['historyById'] = computed(() => (id) => {
    return histories.value.find((history) => history.attachmentId === id)
  })

  const list: DocReviewStore['list'] = async (attachmentIds) => {
    try {
      isListing.value = true
      const response = await apiStore.docReview.list(attachmentIds)
      histories.value = response.histories
    } catch (err) {
      histories.value = []
      console.error(err)
    } finally {
      isListing.value = false
    }
  }

  const version: DocReviewStore['version'] = ref(null)
  const pieceById: DocReviewStore['pieceById'] = computed(() => (id) => version.value?.pieces.find((piece) => piece.pieceId === id))
  const previewRequest = useLatestRequest({
    request: (_, attachmentId: string, refresh: boolean) =>
      apiStore.docReview.preview(attachmentId, refresh),
    onSuccess: (response) => {
      version.value = response.preview ?? null
    },
    onError: (error) => {
      console.error(error)
      throw error
    },
  })
  const isPreviewing = computed(() => previewRequest.loading.value)

  const preview: DocReviewStore['preview'] = async (attachmentId, refresh = false) => {
    if (refresh || version.value?.attachmentId !== attachmentId) {
      files.clear()
      version.value = null
    }
    await previewRequest.run(attachmentId, refresh)
    return version.value
  }
  const cancel = (): void => {
    previewRequest.abort()
  }

  const files = new Map<string, CachedFiles>()

  const getFile: DocReviewStore['getFile'] = async (id) => {
    if (!id) throw new Error('This file has no PDF available.')
    const piece = pieceById.value(id)
    if (piece && (!piece.renditionAttachmentId || piece.errorMessage)) {
      throw new Error(piece.errorMessage || 'This piece has no PDF available.')
    }
    return getCachedFile(id, 'pdf', async () => {
      const attachmentId = piece?.renditionAttachmentId || id
      let filename = piece?.sourceFilename || piece?.label || 'document-piece'
      let field = AttachmentDetail_ContentLocation_Field.FIELD_ORIGINAL
      if (!piece) {
        const attachment = await apiStore.attachment.read(attachmentId)
        filename = attachment.filename
        const isPdf = attachment.mimeType === 'application/pdf' || /\.pdf$/i.test(filename)
        if (!isPdf) {
          const previewField = contentLocationField(attachment.preview?.location)
          if (attachment.preview?.status !== AttachmentDetail_Status.STATUS_READY || previewField === undefined) {
            throw new Error('The uploaded file has no PDF preview available yet.')
          }
          field = previewField
        }
      }
      const content = await apiStore.attachment.readContent(attachmentId, field)
      if (content.location.oneofKind !== 'bytes' || !content.location.bytes.length) {
        throw new Error('The PDF has no content available.')
      }
      return new File(
        [content.location.bytes as BlobPart],
        `${(filename || 'document-piece').replace(/\.[^.]+$/, '')}.pdf`,
        { type: 'application/pdf' },
      )
    })
  }

  function getCachedFile (id: string, format: keyof CachedFiles, read: () => Promise<File>): Promise<File> {
    let entry = files.get(id)
    if (!entry) {
      entry = {}
      files.set(id, entry)
    }
    const cached = entry
    cached[format] ??= read().catch((error: unknown) => {
      delete cached[format]
      throw error
    })
    return cached[format]
  }

  const getOriginalFile: DocReviewStore['getOriginalFile'] = async (id) => {
    const piece = pieceById.value(id)
    const sourceId = piece ? piece.sourceAttachmentId : id
    if (!sourceId) throw new Error('The original file is not available.')
    return getCachedFile(id, 'original', async () => {
      const attachment = await apiStore.attachment.read(sourceId)
      const content = await apiStore.attachment.readContent(sourceId, AttachmentDetail_ContentLocation_Field.FIELD_ORIGINAL)
      if (content.location.oneofKind !== 'bytes' || !content.location.bytes.length) {
        throw new Error('The original file has no content available.')
      }
      return new File([content.location.bytes as BlobPart], piece?.sourceFilename || attachment.filename, { type: attachment.mimeType })
    })
  }

  const runs: Ref<OverrideRun[]> = ref([])
  const runsById = computed(() => (id: string) => {
    return runs.value.find(({ sourceAttachmentId }) => sourceAttachmentId === id)
  })
  const updateRun = (run: OverrideRun): void => {
    const existingIndex = runs.value.findIndex(({ sourceAttachmentId }) => sourceAttachmentId === run.sourceAttachmentId)
    if (existingIndex !== -1) runs.value[existingIndex] = run
    else runs.value.push(run)
  }

  const isRefreshing = ref(false)
  const refresh: DocReviewStore['refresh'] = async (runId: string) => {
    isRefreshing.value = true
    try {
      const { run } = await apiStore.docReview.run(runId)
      if (!run) throw new Error('Override Run not found')
      updateRun(run)
      return run
    } catch (err) {
      console.error(err)
      throw err
    } finally {
      isRefreshing.value = false
    }
  }

  const isSaving = ref(false)
  const save: DocReviewStore['save'] = async (request) => {
    isSaving.value = true
    try {
      const { run } = await apiStore.docReview.save(request)
      if (!run) throw new Error('Failed to start Override Run')
      updateRun(run)
      return run
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      isSaving.value = false
    }
  }

  return {
    // Document version history
    histories,
    historyById,
    isListing,
    list,
    // Currently previewed document version
    version,
    pieceById,
    isPreviewing,
    isSaving,
    getFile,
    getOriginalFile,
    preview,
    save,
    cancel,
    // Document override runs
    isRefreshing,
    runsById,
    refresh,
  }
})

hotReloadStore(useDocReviewStore)
